import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { LanguageContext } from '../App';
import { translations } from '../i18n/translations';
import { apiConfig } from '../config/authConfig';
import { theme } from '../config/theme';

function Admin() {
  const { instance, accounts } = useMsal();
  const { language, setLanguage } = useContext(LanguageContext);
  const t = translations[language];
  const navigate = useNavigate();
  const user = accounts[0];

  const [containers, setContainers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const [newName, setNewName] = useState('');
  const [newGroupId, setNewGroupId] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => { init(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getToken = async () => {
    const tokenResponse = await instance.acquireTokenSilent({
      scopes: [`api://${process.env.REACT_APP_CLIENT_ID}/access_as_user`],
      account: accounts[0],
    });
    return tokenResponse.accessToken;
  };

  const init = async () => {
    try {
      const token = await getToken();
      const adminRes = await fetch(`${apiConfig.baseUrl}/is-admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const adminData = await adminRes.json();
      if (!adminData.isAdmin) {
        setIsAdmin(false);
        setCheckingAdmin(false);
        return;
      }
      setIsAdmin(true);
      setCheckingAdmin(false);
      await fetchConfig(token);
    } catch (err) {
      setError(t.error);
      setCheckingAdmin(false);
    }
  };

  const fetchConfig = async (token) => {
    setLoading(true);
    try {
      const res = await fetch(`${apiConfig.baseUrl}/get-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setContainers(data.containers || {});
    } catch (err) {
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async (updatedContainers) => {
    setSaving(true);
    setFeedback(null);
    try {
      const token = await getToken();
      const res = await fetch(`${apiConfig.baseUrl}/update-config`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ containers: updatedContainers }),
      });
      const data = await res.json();
      if (!res.ok) {
        setFeedback({ type: 'error', message: data.detail || t.error });
        return false;
      }
      setContainers(data.containers || updatedContainers);
      setFeedback({ type: 'success', message: t.adminSaved });
      return true;
    } catch (err) {
      setFeedback({ type: 'error', message: t.error });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    const name = newName.trim().toLowerCase();
    const groupId = newGroupId.trim();
    if (!name || !groupId) {
      setFeedback({ type: 'error', message: t.adminFillBoth });
      return;
    }
    if (containers[name]) {
      setFeedback({ type: 'error', message: t.adminAlreadyExists });
      return;
    }
    const updated = { ...containers, [name]: groupId };
    const ok = await saveConfig(updated);
    if (ok) {
      setNewName('');
      setNewGroupId('');
    }
  };

  const handleDelete = async (name) => {
    const updated = { ...containers };
    delete updated[name];
    await saveConfig(updated);
  };

  const handleLogout = () => instance.logoutRedirect().catch(console.error);

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.logoGrid}>
            {[...Array(9)].map((_, i) => <span key={i} style={styles.logoCell} />)}
          </div>
          <span style={styles.headerTitle}>Archives — Admin</span>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.langSelector}>
            <button onClick={() => setLanguage('fr')}
              style={language === 'fr' ? styles.langActive : styles.langBtn}>FR</button>
            <button onClick={() => setLanguage('en')}
              style={language === 'en' ? styles.langActive : styles.langBtn}>EN</button>
          </div>
          <span style={styles.userName}>{user?.name || user?.username}</span>
          <button onClick={handleLogout} style={styles.logoutBtn}>{t.logout}</button>
        </div>
      </header>

      <main style={styles.main}>
        <div style={styles.topBar}>
          <button onClick={() => navigate('/home')} style={styles.backBtn}>← {t.back}</button>
        </div>

        <h1 style={styles.title}>{t.adminTitle}</h1>
        <p style={styles.subtitle}>{t.adminSubtitle}</p>

        {checkingAdmin && <p style={styles.info}>{t.loading}</p>}

        {!checkingAdmin && !isAdmin && (
          <div style={styles.deniedBox}>
            <p style={styles.deniedText}>{t.adminDenied}</p>
          </div>
        )}

        {!checkingAdmin && isAdmin && (
          <>
            <div style={styles.sectionTitle}>{t.adminExistingContainers}</div>

            {loading && <p style={styles.info}>{t.loading}</p>}
            {error && <p style={styles.errorMsg}>{error}</p>}

            {!loading && Object.keys(containers).length === 0 && (
              <p style={styles.info}>{t.adminNoContainers}</p>
            )}

            {!loading && Object.keys(containers).length > 0 && (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>{t.adminContainerName}</th>
                    <th style={styles.th}>{t.adminGroupId}</th>
                    <th style={styles.thActions}></th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(containers).map(([name, groupId]) => (
                    <tr key={name} style={styles.tr}>
                      <td style={styles.td}>
                        <span style={styles.containerBadge}>{name}</span>
                      </td>
                      <td style={styles.tdMono}>{groupId}</td>
                      <td style={styles.tdActions}>
                        <button onClick={() => handleDelete(name)}
                          disabled={saving} style={styles.deleteBtn}>
                          {t.adminDelete}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div style={styles.sectionTitle}>{t.adminAddContainer}</div>

            <div style={styles.addForm}>
              <div style={styles.field}>
                <label style={styles.label}>{t.adminContainerName}</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="ex: rh"
                  style={styles.input}
                />
                <span style={styles.hint}>{t.adminNameHint}</span>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>{t.adminGroupId}</label>
                <input
                  type="text"
                  value={newGroupId}
                  onChange={(e) => setNewGroupId(e.target.value)}
                  placeholder="ex: 7cff7898-26d3-45a9-88d5-57e6e50d408a"
                  style={styles.input}
                />
                <span style={styles.hint}>{t.adminGroupHint}</span>
              </div>
              <button onClick={handleAdd} disabled={saving} style={styles.addBtn}>
                {saving ? t.adminSaving : t.adminAdd}
              </button>
            </div>

            {feedback && (
              <div style={feedback.type === 'success' ? styles.feedbackSuccess : styles.feedbackError}>
                {feedback.message}
              </div>
            )}

            <div style={styles.warningBox}>
              <p style={styles.warningText}>{t.adminWarning}</p>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: theme.colors.bg },
  header: {
    backgroundColor: theme.colors.bgHeader,
    padding: '0 24px', height: '56px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.colors.border}`,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  logoGrid: {
    width: '28px', height: '28px', backgroundColor: theme.colors.white,
    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gridTemplateRows: '1fr 1fr 1fr',
    gap: '2px', padding: '4px', borderRadius: '2px',
  },
  logoCell: { backgroundColor: theme.colors.textDark, display: 'block', borderRadius: '1px' },
  headerTitle: {
    color: theme.colors.white, fontSize: theme.fonts.size.base,
    fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase',
  },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  langSelector: { display: 'flex', gap: '4px' },
  langBtn: {
    border: `1px solid ${theme.colors.border}`, borderRadius: '3px',
    padding: '3px 8px', cursor: 'pointer', backgroundColor: 'transparent',
    color: theme.colors.textSecondary, fontSize: theme.fonts.size.xs,
  },
  langActive: {
    border: `1px solid ${theme.colors.white}`, borderRadius: '3px',
    padding: '3px 8px', cursor: 'pointer', backgroundColor: theme.colors.white,
    color: theme.colors.textDark, fontSize: theme.fonts.size.xs, fontWeight: '600',
  },
  userName: { color: theme.colors.textSecondary, fontSize: theme.fonts.size.base },
  logoutBtn: {
    backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px', color: theme.colors.textSecondary,
    padding: '5px 12px', cursor: 'pointer', fontSize: theme.fonts.size.sm,
  },
  main: { padding: '32px', maxWidth: '900px' },
  topBar: { marginBottom: '20px' },
  backBtn: {
    backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px', color: theme.colors.textSecondary,
    padding: '6px 14px', cursor: 'pointer', fontSize: theme.fonts.size.base,
  },
  title: { fontSize: '26px', fontWeight: '500', color: theme.colors.textPrimary, marginBottom: '6px' },
  subtitle: { color: theme.colors.textSecondary, fontSize: theme.fonts.size.lg, marginBottom: '36px' },
  sectionTitle: {
    color: theme.colors.textSecondary, fontSize: theme.fonts.size.xs,
    fontWeight: '600', letterSpacing: '0.12em', textTransform: 'uppercase',
    marginBottom: '16px', marginTop: '32px',
  },
  info: { color: theme.colors.textSecondary, fontSize: theme.fonts.size.base },
  errorMsg: { color: '#e74c3c', fontSize: theme.fonts.size.base },
  deniedBox: {
    backgroundColor: '#2a1111', border: '1px solid #7a3030',
    borderRadius: '8px', padding: '20px', marginTop: '20px',
  },
  deniedText: { color: '#e74c3c', fontSize: theme.fonts.size.base, margin: 0 },
  table: { width: '100%', borderCollapse: 'collapse', marginBottom: '16px' },
  th: {
    textAlign: 'left', padding: '10px 14px', fontSize: theme.fonts.size.xs,
    fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase',
    color: theme.colors.textSecondary, borderBottom: `1px solid ${theme.colors.border}`,
  },
  thActions: { borderBottom: `1px solid ${theme.colors.border}`, width: '100px' },
  tr: { borderBottom: `1px solid ${theme.colors.divider}` },
  td: { padding: '12px 14px', fontSize: theme.fonts.size.base, color: theme.colors.textPrimary },
  tdMono: {
    padding: '12px 14px', fontSize: theme.fonts.size.sm,
    color: theme.colors.textSecondary, fontFamily: 'monospace',
  },
  tdActions: { padding: '12px 14px', textAlign: 'right' },
  containerBadge: {
    backgroundColor: theme.colors.bgCardHover, color: theme.colors.accent,
    padding: '3px 10px', borderRadius: '4px', fontSize: theme.fonts.size.sm,
    fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em',
  },
  deleteBtn: {
    backgroundColor: '#c0392b', color: theme.colors.white,
    border: 'none', borderRadius: '4px', padding: '5px 12px',
    cursor: 'pointer', fontSize: theme.fonts.size.sm,
  },
  addForm: {
    backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.border}`,
    borderRadius: '8px', padding: '20px', display: 'flex',
    flexDirection: 'column', gap: '16px', maxWidth: '600px',
  },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: {
    color: theme.colors.textSecondary, fontSize: theme.fonts.size.sm, fontWeight: '500',
  },
  input: {
    backgroundColor: theme.colors.bgCardHover, border: `1px solid ${theme.colors.border}`,
    borderRadius: '6px', padding: '8px 12px', fontSize: theme.fonts.size.base,
    color: theme.colors.textPrimary, outline: 'none',
  },
  hint: { color: theme.colors.textMuted, fontSize: theme.fonts.size.xs },
  addBtn: {
    backgroundColor: theme.colors.white, color: theme.colors.textDark,
    border: 'none', borderRadius: '6px', padding: '10px 20px',
    cursor: 'pointer', fontSize: theme.fonts.size.base, fontWeight: '600',
    alignSelf: 'flex-start',
  },
  feedbackSuccess: {
    backgroundColor: '#0f2a17', border: '1px solid #2a7a4a', color: '#6fcf97',
    borderRadius: '6px', padding: '12px 16px', marginTop: '16px',
    fontSize: theme.fonts.size.base,
  },
  feedbackError: {
    backgroundColor: '#2a1111', border: '1px solid #7a3030', color: '#e74c3c',
    borderRadius: '6px', padding: '12px 16px', marginTop: '16px',
    fontSize: theme.fonts.size.base,
  },
  warningBox: {
    backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.border}`,
    borderRadius: '8px', padding: '16px', marginTop: '32px',
  },
  warningText: {
    color: theme.colors.textMuted, fontSize: theme.fonts.size.sm, margin: 0, lineHeight: '1.6',
  },
};

export default Admin;